import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Modal, Form } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import { useAuth } from '../../context/AuthContext';
import withPermission from '../../utils/withPermission';
import Swal from 'sweetalert2';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const API_URL = `${backendUrl}/api/industries`;

const IndustryDetails = () => {
    const [industries, setIndustries] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ name: '' });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedIndustry, setSelectedIndustry] = useState(null);
    const didFetchRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const WithListPermission = withPermission(({ children }) => children, ['industry-list']);
    const WithCreatePermission = withPermission(({ children }) => children, ['industry-create']);
    const WithDeletePermission = withPermission(({ children }) => children, ['industry-delete']);
    const WithEditPermission = withPermission(({ children }) => children, ['industry-edit']);
    const [searchQuery, setSearchQuery] = useState('');
    const [errors, setErrors] = useState({});

    const debounceTimeoutRef = useRef(null);
     useEffect(() => {
           if (debounceTimeoutRef.current) {
               clearTimeout(debounceTimeoutRef.current);
           }
   
           debounceTimeoutRef.current = setTimeout(() => {
               fetchIndustries(1, searchQuery);
           }, searchQuery ? 1500 : 0); 
   
           return () => {
               if (debounceTimeoutRef.current) {
                   clearTimeout(debounceTimeoutRef.current);
               }
           };
       }, [searchQuery]);
       const handleSearchChange = (e) => {
           setSearchQuery(e.target.value);
   
       };   

    const fetchIndustries = async (page = 1) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');

            const token = userInfo?.token;
            const response = await axios.get(`${API_URL}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    page,
                    search: searchQuery,
                },
            });
            setIndustries(response.data.industry);
            setPageCount(response.data.last_page);
            setCurrentPage(response.data.current_page);


        } catch (error) {
            console.error('Error fetching industries:', error);
        }
    };

    const handleAddIndustry = () => {
        setFormData({ name: '' });
        setIsEdit(false);
        setSelectedIndustry(null);
        setShowModal(true);
    };

    const handleEditIndustry = (industry) => {
        setFormData(industry);
        setIsEdit(true);
        setSelectedIndustry(industry);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
            try {
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const token = userInfo?.token;
                await axios.delete(`${API_URL}/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setIndustries(industries.filter(industry => industry.id !== id));
                Swal.fire(
                    "Deleted!",
                    "The industry has been deleted.",
                    "success"
                );
            } catch (error) {
                console.error('Error deleting industry:', error);
                Swal.fire(
                    "Error!",
                    "Failed to delete industry. Please try again.",
                    "error"
                );
            }
        }
        });
        
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchIndustries(newPage);
    };
    useEffect(() => {
        if (!showModal) {
            setErrors({});
        }
    }, [showModal]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = 'Name is required';
      
        
        return newErrors;
      };
      const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        const userInfo = JSON.parse(localStorage.getItem('user-info'));
        const token = userInfo?.token;
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setLoading(false);
            return; // Stop here if validation fails
        }
        try {
            if (isEdit) {
                const response = await axios.put(`${API_URL}/${selectedIndustry.id}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setIndustries(industries.map(ind => (ind.id === selectedIndustry.id ? response.data.industry : ind)));
            } else {
                const response = await axios.post(API_URL, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setIndustries([...industries, response.data.industry]);
            }
            setShowModal(false);
        } catch (error) {
            console.error('Error saving industry:', error);
        }
    };
       const handleImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(
                `${backendUrl}/api/industries/import`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                        'Tenant-Origin': origin,
                    },
                }
            );

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: response.data.message || ` Imported successfully!`,
                timer: 3000,
                timerProgressBar: false,
                showConfirmButton: false,
                didOpen: () => {
                    const container = Swal.getContainer();
                    container.style.top = '50px';
                },
            });

            // Refresh the list after import
            setTimeout(() => window.location.reload(), 1000);

            // Clear the file input
            event.target.value = '';

        } catch (error) {
            console.error('Import error:', error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: error.response?.data?.errors || error.response?.errors || `Failed to import instance information`,
                timer: 3000,
                timerProgressBar: false,
                showConfirmButton: false,
                didOpen: () => {
                    const container = Swal.getContainer();
                    container.style.top = '50px';
                },
            });
            event.target.value = "";

        } finally {
        }
    };

    // Export Functions
    const handleExport = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(
                `${backendUrl}/api/industries/export`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                    responseType: 'blob', // Important for file download
                }
            );

            // Create blob link and download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Get filename from response headers or use default
            const contentDisposition = response.headers['content-disposition'];
            let filename = `industries_${new Date().toISOString().split('T')[0]}.xlsx`;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                if (filenameMatch.length === 2) {
                    filename = filenameMatch[1];
                }
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: `Exported successfully!`,
                timer: 2000,
                timerProgressBar: false,
                showConfirmButton: false,
                didOpen: () => {
                    const container = Swal.getContainer();
                    container.style.top = '50px';
                },
            });

        } catch (error) {
            console.error('Export error:', error);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: `Failed to export`,
                timer: 3000,
                timerProgressBar: false,
                showConfirmButton: false,
                didOpen: () => {
                    const container = Swal.getContainer();
                    container.style.top = '50px';
                },
            });
        } finally {
        }
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid ">
                    <div className="row w-100">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Industry</h4>
                                <div className="page-title-right">
                                    <ol className="breadcrumb m-0">
                                        <li className="breadcrumb-item"><a href="javascript:void(0);">Industry</a></li>
                                        <li className="breadcrumb-item active">Industry List</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                <div className="d-flex justify-content-between mb-3">
                                        <div className="d-flex">
                                            <div className="search-box me-2 mb-2 d-inline-block">
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Search..."
                                                        value={searchQuery}
                                                        onChange={handleSearchChange}
                                                    />
                                                    <i className="bx bx-search-alt search-icon" />
                                                </div>
                                            </div>



                                        </div>
                                         <div className="d-flex align-items-center gap-2">
                                            {/* Export Button */}
                                            <button
                                                type="button"
                                                className="btn btn-light border"
                                                onClick={handleExport}
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Export"
                                            >
                                                <i className="mdi mdi-file-excel text-success"></i>
                                            </button>

                                            {/* Import Button */}
                                            <input
                                                type="file"
                                                accept=".xlsx, .xls"
                                                id="importLeadsFile"
                                                onChange={handleImport}
                                                style={{ display: "none" }}
                                            />
                                            <label
                                                htmlFor="importLeadsFile"
                                                className="btn btn-light border mb-0"
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Import"
                                            >
                                                <i className="mdi mdi-upload text-primary"></i>
                                            </label>

                                                                               <WithCreatePermission>
                                                                               <button
                                                    type="button"
                                                    className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                                   
                                                    onClick={handleAddIndustry}
                                                    data-bs-toggle="tooltip"
                                                    data-bs-placement="top"
                                                    title='Add Industry'
                                                    >
                                                    <i className="mdi mdi-plus"></i>
                                                </button></WithCreatePermission>
                                                </div>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table align-middle table-nowrap table-check table-list">

                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: '20px' }} className="align-middle">
                                                        <div className="form-check font-size-16">
                                                            <input className="form-check-input" type="checkbox" id="checkAll" />
                                                            <label className="form-check-label" htmlFor="checkAll"></label>
                                                        </div>
                                                    </th>
                                                    <th className="align-middle">#</th>
                                                    <th className="align-middle"> Name</th>
                                                    <th className="align-middle">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {industries.map((industry, index) => (
                                                    <tr key={industry.id}>
                                                        <td>
                                                            <div className="form-check font-size-16">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    id={`instanceCheck${index}`}
                                                                />
                                                                <label
                                                                    className="form-check-label"
                                                                    htmlFor={`instanceCheck${index}`}
                                                                ></label>
                                                            </div>
                                                        </td>
                                                        <td>{index + 1}</td>
                                                        <td>{industry.name}</td>
                                                        <td>
                                                            <div className="dropdown">
                                                                <i
                                                                    className="mdi mdi-dots-horizontal font-size-18"
                                                                    data-bs-toggle="dropdown"
                                                                    aria-expanded="false"
                                                                    style={{ cursor: 'pointer' }}
                                                                ></i>
                                                                <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                                    <li>
                                                                        <WithEditPermission>
                                                                        <button className="dropdown-item" onClick={() => handleEditIndustry(industry)}>
                                                                            Edit
                                                                        </button>
                                                                        </WithEditPermission>
                                                                    </li>
                                                                    <li>
                                                                    <WithDeletePermission>
                                                                        <button className="dropdown-item" onClick={() => handleDelete(industry.id)}>
                                                                            Delete
                                                                        </button>
                                                                        </WithDeletePermission>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="d-flex justify-content-end pagination-sm">
                                    <ReactPaginate
                            previousLabel={'<'}
                            nextLabel={'>'}
                            breakLabel={'...'}
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={3}
                            onPageChange={handlePageChange}
                            containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                            pageClassName={'page-item'}
                            pageLinkClassName={'page-link'}
                            previousClassName={'page-item previous'}
                            previousLinkClassName={'page-link'}
                            nextClassName={'page-item next'}
                            nextLinkClassName={'page-link'}
                            breakClassName={'page-item'}
                            breakLinkClassName={'page-link'}
                            activeClassName={'active'}
                        />
                                    </div>
                                    {/* Modal for Add/Edit Industry */}
                                    <Modal show={showModal} onHide={() => setShowModal(false)}>
                                        <Modal.Header closeButton>
                                            <Modal.Title>{isEdit ? 'Edit Industry' : 'Add Industry'}</Modal.Title>
                                        </Modal.Header>
                                        <Modal.Body>
                                            <Form>
                                                <Form.Group controlId="industryName">
                                                    <Form.Label>Name</Form.Label>
                                                    <Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange}
                                                      className={`form-control ${errors?.name ? 'is-invalid' : ''}`}

                                                      />
                                                      {errors.name && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.name}</div>}
                                                </Form.Group>
                                            </Form>
                                        </Modal.Body>
                                        <Modal.Footer>
                                            <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
                                            <Button variant="secondary" onClick={handleSubmit}>{isEdit ? 'Update' : 'Save'} </Button>
                                        </Modal.Footer>
                                    </Modal>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IndustryDetails;
